import { ReactNode } from 'react';

import { Box, Divider, Typography } from '@mui/material';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { useBreakpoints } from '@/hooks/useBreakpoints';

const reCaptchaKey: string = import.meta.env.VITE_REACT_RE_CAPTCHA_KEY;

const logos = [
  { src: '/img/logo/bloomberg.svg', alt: 'Bloomberg' },
  { src: '/img/logo/exxon-mobil.svg', alt: 'Exxon Mobil' },
  { src: '/img/logo/totalenergies-logo.png', alt: 'Total Energies' },
  { src: '/img/logo/woodmackenzie.svg', alt: 'Woodmackenzie' },
  { src: '/img/logo/bp.svg', alt: 'BP' }
];

const LogoGrid = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        mt: 4,
        gap: 4, // 🔹 equal spacing between cells
        justifyContent: 'space-evenly',
        alignItems: 'center',

        img: {
          height: {
            xs: '12px',
            lg: '19px'
          }
        }
      }}
    >
      {logos.map(logo => (
        <img
          key={logo.src}
          alt={logo.alt}
          decoding='async'
          fetchPriority='low'
          loading='lazy'
          src={logo.src}
        />
      ))}
    </Box>
  );
};

const OnboardingImageScreen = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        height: '100vh',
        backgroundColor: 'background.default',
        overflow: 'hidden',
        position: 'sticky',
        top: 0
      }}
    >
      <Box
        sx={{
          py: '10vh',
          px: '3vw',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8vh',
          flex: 1
        }}
      >
        <Box sx={{ overflow: 'hidden', width: '100%', transform: 'translateX(5vw)' }}>
          <img
            alt='GX GO application preview'
            decoding='async'
            fetchPriority='high'
            loading='eager'
            src='/img/login-image.png'
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'right'
            }}
          />
        </Box>
        <Box
          sx={{
            width: '100%'
          }}
        >
          <Divider
            sx={{
              '&::before': { borderTop: '1px solid #F8F8F8' },
              '&::after': { borderTop: '1px solid #F8F8F8' },
              margin: 0
            }}
            variant='middle'
          >
            <Typography>Trusted By</Typography>
          </Divider>
          <LogoGrid />
        </Box>
      </Box>
    </Box>
  );
};
const OnboardingLayout = ({ children }: { children: ReactNode }) => {
  const { isDesktop } = useBreakpoints();

  return (
    <GoogleReCaptchaProvider
      container={{
        element: 'onboarding-recaptcha',
        parameters: {
          badge: 'bottomleft',
          theme: 'dark'
        }
      }}
      reCaptchaKey={reCaptchaKey}
    >
      <Box
        sx={{
          backgroundImage: 'linear-gradient(244deg, #121836 0%, #070818 99.27%) !important',
          width: 'fit-content',
          minWidth: '100%'
        }}
      >
        <Box
          sx={{
            minHeight: '100vh',
            minWidth: '300px',
            display: 'flex',
            justifyContent: 'center',
            flex: 1,
            position: 'relative'
          }}
        >
          <Box
            sx={{
              flex: 1
            }}
          >
            <Box
              sx={{
                py: '8%',
                pl: 4,
                pr: {
                  xs: 0,
                  md: 4
                }
              }}
            >
              {children}
            </Box>
          </Box>
          {isDesktop && <OnboardingImageScreen />}
        </Box>
      </Box>
    </GoogleReCaptchaProvider>
  );
};

export default OnboardingLayout;
